// File: view/pages/Ai/AIChat.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaBookBookmark } from 'react-icons/fa6';
import { Paperclip } from 'react-feather';

/* ------------------------- Query-string helper ------------------------- */
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/* --------- Minimal RichText: **bold**, - bullets, 1. numbered --------- */
function renderBold(str) {
  return str.split(/(\*\*[^*]+\*\*)/g).map((seg, i) => {
    const m = seg.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={i} style={{ fontWeight: 600 }}>{m[1]}</strong>;
    return <span key={i}>{seg}</span>;
  });
}
function preprocessForLists(text) {
  if (!text) return '';
  let s = String(text);
  s = s.replace(/\s-\s+/g, '\n- ');
  s = s.replace(/\s(\d+)\.\s+/g, '\n$1. ');
  return s;
}
function RichText({ text, color = 'inherit' }) {
  if (!text) return null;

  const wrap = { wordBreak: 'break-word', overflowWrap: 'anywhere' };

  const stylesRT = {
 p:  { margin: '0 0 6px 0', lineHeight: 1.35, color, ...wrap },
 ul: { margin: '4px 0 0 16px', padding: 0, color, ...wrap },
 ol: { margin: '4px 0 0 16px', padding: 0, color, ...wrap }
  };
  const prepped = preprocessForLists(text);
  const lines = prepped.split(/\n+/);
  const nodes = [];
  let listType = null; // 'ul' | 'ol'
  let listItems = [];
  const flushList = (keyBase) => {
    if (!listType || !listItems.length) return;
    if (listType === 'ul') {
      nodes.push(
        <ul key={`ul-${keyBase}`} style={stylesRT.ul}>
{listItems.map((li, i) => <li key={i} style={wrap}>{renderBold(li)}</li>)}

        </ul>
      );
    } else {
      nodes.push(
        <ol key={`ol-${keyBase}`} style={stylesRT.ol}>
{listItems.map((li, i) => <li key={i} style={wrap}>{renderBold(li)}</li>)}
        </ol>
      );
    }
    listType = null; listItems = [];
  };
  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) return;
    if (/^-\s+/.test(line)) {
      const content = line.replace(/^-+\s+/, '');
      if (listType && listType !== 'ul') flushList(idx);
      listType = 'ul'; listItems.push(content); return;
    }
    const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numMatch) {
      const content = numMatch[2];
      if (listType && listType !== 'ol') flushList(idx);
      listType = 'ol'; listItems.push(content); return;
    }
    flushList(idx);
    nodes.push(<p key={`p-${idx}`} style={stylesRT.p}>{renderBold(line)}</p>);
  });
  flushList('end');
  return <>{nodes}</>;
}




// for s3
// robust S3 -> UI attachment mapping (handles missing/incorrect MIME)
function mapAttachmentsFromHistory(atts = []) {
  return atts.map(a => {
    const mime = (a.mimetype || '').toLowerCase();
    const nameKey = (a.name || a.key || '').toLowerCase();

    const isImg = mime.startsWith('image/')
      || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(nameKey);

    const isAud = mime.startsWith('audio/')
      || /\.(mp3|m4a|aac|wav|ogg|oga|flac|opus)$/.test(nameKey);

    return {
      name: a.name || a.key || 'file',
      url: a.url,
      type: isImg ? 'image' : (isAud ? 'audio' : 'file')
    };
  });
}


// function mapAttachmentsFromHistory(atts = []) {
//   return atts.map(a => {
//     const mime = (a.mimetype || '').toLowerCase();
//     const isImg = mime.startsWith('image/');
//     const isAud = mime.startsWith('audio/');
//     return {
//       name: a.name || a.key || 'file',
//       url: a.url,                     // presigned URL from backend
//       type: isImg ? 'image' : (isAud ? 'audio' : 'file')
//     };
//   });
// }



/* ------------------------------ Component ----------------------------- */
const NOT_FOUND_RE = /no\s+user\s+in\s+crm\s+found/i;

export default function AIChat() {
  const { id: sessionIdFromRoute } = useParams();
  const query = useQuery();
  const phone = query.get('phone') || '';
   const to = query.get('to') || '';
  const from = query.get('from') || '';

  const [messages, setMessages] = useState([]);
  const [headerText, setHeaderText] = useState('');
  const [hasHeaderData, setHasHeaderData] = useState(false);

  const [bootLoading, setBootLoading] = useState(false); // overlay while fetching history
  const [seedLoading, setSeedLoading] = useState(false); // initial agent-query only
  const [loading, setLoading] = useState(false);         // subsequent "Typing..."
  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
const fileInputRef = useRef(null);

const makeAttachmentPreviews = (files) => Array.from(files).map(f => {
  const url = URL.createObjectURL(f);
  const t = (f.type || '').toLowerCase();
  const isImg = t.startsWith('image/'); const isAud = t.startsWith('audio/');
  return { name: f.name, url, type: isImg ? 'image' : (isAud ? 'audio' : 'file') };
});
const clearFileInput = () => { setSelectedFiles([]); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const bottomRef = useRef(null);

  // Toggle between prod/local easily
  const BASE = process.env.REACT_APP_BOT_BASE || 'https://bot.mergeasy.com'; //for production
  // const BASE = 'http://localhost:8000'; //for local testing

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  };

  useEffect(() => {
    fetchHistoryOrSeed(sessionIdFromRoute, phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdFromRoute, phone]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, seedLoading]);

  const maybeSetHeaderFromText = (txt) => {
    if (!txt) return;
    if (!NOT_FOUND_RE.test(String(txt))) {
      setHeaderText(String(txt));
      setHasHeaderData(true);
    }
  };

  async function fetchHistoryOrSeed(sessionId, phoneForSeed) {
    setBootLoading(true);
    try {
      const hist = await axios.post(
        `${BASE}/chat/session-history`,
        { session_id: sessionId },
        { headers: authHeaders }
      );
      const items = hist?.data?.items || [];
      if (Array.isArray(items) && items.length > 0) {
        const mapped = [];
        for (const it of items) {
          // without s3
          // if (it.query) mapped.push({ sender: 'user', text: it.query });
          // if (it.response) {
          //   mapped.push({
          //     sender: 'bot',
          //     text: it.response,
          //     response_id: it.id,
          //     podio_refs: it.podio_refs,
          //     saved: false
          //   });
          // }

// for s3
const hasAtts = Array.isArray(it.attachments) && it.attachments.length > 0;
if ((it.query && it.query.trim() !== '') || hasAtts) {
  mapped.push({
    sender: 'user',
    text: it.query || '',
    attachments: mapAttachmentsFromHistory(it.attachments || [])
  });
}





if (it.response) {
  mapped.push({
    sender: 'bot',
    text: it.response,
    response_id: it.id,
    podio_refs: it.podio_refs,
    saved: false
  });
}
        }
        setMessages(mapped);
        const firstBot = mapped.find(m => m.sender === 'bot');
        if (firstBot) maybeSetHeaderFromText(firstBot.text);
        setBootLoading(false);
      } else {
        setBootLoading(false);
        if (phoneForSeed) {
          await seedInitialQuery(sessionId, phoneForSeed);
        }
      }
    } catch (e) {
      console.error('history error', e);
      setBootLoading(false);
      if (phoneForSeed) {
        await seedInitialQuery(sessionId, phoneForSeed);
      }
    }
  }

  async function seedInitialQuery(sessionId, phoneForSeed) {
    const text = `Tell the details of the item whose phone number is ${phoneForSeed}, if found nothing then send Just mentioning - No User In CRM Found.?`;
    setSeedLoading(true);
    setLoading(true);
    try {


      const res = await axios.post(
        `${BASE}/agent-query`,
        { text, session_id: sessionId, to,from,ai_sense:"ai_chat_bot" },
        { headers: authHeaders }
      );
      const botMsg = res?.data?.response || 'No response';
      const podioRefs = res?.data?.podio_refs || null;
      const responseId = res?.data?.response_id || null;
      // Only bot message for seed
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: botMsg,
        podio_refs: podioRefs,
        response_id: responseId,
        saved: false
      }]);
      maybeSetHeaderFromText(botMsg);
    } catch (e) {
      console.error('seed error', e);
      setMessages(prev => [...prev, { sender: 'bot', text: 'Something went wrong while starting the session.' }]);
    } finally {
      setSeedLoading(false);
      setLoading(false);
    }
  }

  async function sendQuery(text) {
    setLoading(true);
    try {
 

      let res;
  if (selectedFiles.length > 0) {
    const form = new FormData();
    form.append('text', text || '');
    form.append('session_id', sessionIdFromRoute);
    form.append('to', to || '');
    form.append('from', from || '');
    form.append('ai_sense', "ai_chat_bot");
    selectedFiles.forEach(f => form.append('files', f, f.name));
    const previews = makeAttachmentPreviews(selectedFiles);
    setMessages(prev => [...prev, { sender: 'user', text, attachments: previews }]);
    res = await axios.post(`${BASE}/agent-query`, form, { headers: { 'Authorization': authHeaders.Authorization } });
    clearFileInput();
  } else {



    setMessages(prev => [...prev, { sender: 'user', text }]);
    res = await axios.post(`${BASE}/agent-query`, { text, session_id: sessionIdFromRoute, to, from, ai_sense:"ai_chat_bot" }, { headers: authHeaders });
  }

      const botMsg = res?.data?.response || 'No response';
      const podioRefs = res?.data?.podio_refs || null;
      const responseId = res?.data?.response_id || null;
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: botMsg,
        podio_refs: podioRefs,
        response_id: responseId,
        saved: false
      }]);
    //   if (!hasHeaderData) maybeSetHeaderFromText(botMsg);
    } catch (e) {
      console.error('send error', e);
      setMessages(prev => [...prev, { sender: 'bot', text: 'Something went wrong.' }]);
    } finally {
      setLoading(false);
      setSelectedFiles([])
    }
  }

  async function saveToKB(responseId, podioRefs, index) {
    try {
      const res = await axios.post(
        `${BASE}/podio/index-by-response`,
        { response_id: responseId, podio_refs: podioRefs },
        { headers: authHeaders }
      );
      if (res.status === 200) {
        const next = [...messages];
        next[index].saved = true;
        setMessages(next);
      }
    } catch (e) {
      console.error('KB error', e);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Top Bar */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#111827' }}>✨🤖 AI Assistant</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Session: {sessionIdFromRoute}</div>
        </div>
        {/* room for future actions */}
      </div>

      {/* Dynamic header card */}
      <div style={{ padding: 16 }}>
        {hasHeaderData && (
          <div style={{
            maxWidth: 960,
            margin: '0 auto',
            background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)',
            borderRadius: 14,
            padding: 16,
            border: '1px solid #e5e7eb',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              left: 0, top: 0, height: '100%', width: 8,
              background: 'linear-gradient(180deg, #6366f1 0%, #22d3ee 100%)',
              borderTopLeftRadius: 14, borderBottomLeftRadius: 14
            }} />
            <div style={{ marginLeft: 16 }}>
              <RichText text={headerText} color="#111827" />
            </div>
          </div>
        )}
      </div>

      {/* Chat body */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '8px 12px' }}>
        <div style={{
          width: '100%', maxWidth: 960,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative'
        }}>
          {/* Session-history overlay */}
          {bootLoading && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.85)', zIndex: 10
            }}>
              <div style={{
                width: 48, height: 48, border: '4px solid #e5e7eb',
                borderTop: '4px solid #6366f1', borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          )}

          {/* Messages */}
          <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
            {messages.map((m, idx) => (
              <div key={idx} style={{ marginBottom: 12, textAlign: m.sender === 'user' ? 'right' : 'left' }}>
                <div style={{
                  display: 'inline-block',
                  padding: '10px 14px',
                  borderRadius: 16,
                  background: m.sender === 'user' ? '#2563eb' : '#e5e7eb',
                  color: m.sender === 'user' ? '#fff' : '#111827',
                  maxWidth: '80%',
                  fontSize: 15,
                  lineHeight: 1.4,
                  boxShadow: m.sender === 'user'
                    ? '0 6px 16px rgba(37,99,235,0.25)'
                    : '0 4px 12px rgba(0,0,0,0.06)',
                      wordBreak: 'break-word',
 overflowWrap: 'anywhere',
 minWidth: 0
                }}>
                  <RichText text={m.text} />

                  {m.attachments && m.attachments.length > 0 && (
  <div style={{ marginTop: 8 }}>
    {m.attachments.map((a, i) => {
      if (a.type === 'image') {
        return <img key={i} src={a.url} alt={a.name} style={{ maxWidth: 280, borderRadius: 10, display: 'block', marginTop: 8 }} />;
      } else if (a.type === 'audio') {
        return  <audio
    key={i}
    controls
    preload="metadata"
    src={a.url}
    style={{ display: 'block', marginTop: 6, maxWidth: 240 }}
  />
        // <audio key={i} controls src={a.url} style={{ display: 'block', marginTop: 8, maxWidth: 360 }} />;








 














      }
      return <a
  href={a.url}
  target="_blank"
  rel="noreferrer"
  style={{ color: '#fff', textDecoration: 'underline', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
>
  {a.name}
</a>
    })}
  </div>
)}
                </div>

                {m.sender === 'bot' && m.response_id && !m.saved && (
                  <div
                    onClick={() => saveToKB(m.response_id, m.podio_refs, idx)}
                    title="Save this answer to knowledgebase"
                    style={{
                      cursor: 'pointer', fontSize: 12, marginTop: 6,
                      display: 'flex', gap: 6, alignItems: 'center', color: '#2563eb'
                    }}
                  >
                    <FaBookBookmark size={14} />
                    <span>Save to KB</span>
                  </div>
                )}
              </div>
            ))}

            {/* Waiting texts */}
            {seedLoading && (
              <div style={{ fontStyle: 'italic', color: '#6b7280', fontSize: 14 }}>
                Checking CRM for details related to this number…
              </div>
            )}
            {!seedLoading && loading && (
              <div style={{ fontStyle: 'italic', color: '#6b7280', fontSize: 14 }}>
                Typing...
              </div>
            )}

            <div ref={bottomRef} />
          </div>

         

          <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', background: '#fff' }}>
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <button
      type="button"
      title="Attach files"
      onClick={() => fileInputRef.current?.click()}
      style={{
        width: 40, height: 40, borderRadius: '50%', border: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg,#4144f5 0%, #37bcfa 100%)',
        boxShadow: '0 8px 16px rgba(0,0,0,0.15)', cursor: 'pointer'
      }}
    >
      <Paperclip size={18} color="#fff" />
    </button>
    <input
      value={input}
      onChange={(e) => setInput(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !loading && (input.trim() || selectedFiles.length > 0)) {
          sendQuery(input.trim());
          setInput('');
        }
      }}
      placeholder="Type a message…"
      disabled={loading}
      style={{ flex: 1, padding: 12, borderRadius: 28, border: '1px solid #e5e7eb', fontSize: 15 }}
    />
    <input
      ref={fileInputRef}
      type="file"
      multiple
      style={{ display: 'none' }}
      accept="image/*,audio/*"
      onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
      // To allow ALL file types, remove the `accept` line above or set: accept="*/*"
    />
  </div>
  {selectedFiles.length > 0 && (
    <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
      {selectedFiles.length} file(s) selected
    </div>
  )}
</div>


        </div>
      </div>

      {/* Spinner keyframes */}
      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

